'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import { AUTH_CHANGE_EVENT, isNormalUserSession } from '@/lib/auth'
import {
  getThread,
  postCreateThread,
  postChatMessage,
  postSelectOption,
  postCloseThread,
  getThreadListForUser,
  postClaimThread,
} from '@/lib/support/api'
import { clearLegacySupportGuestLocalStorage } from '@/lib/support/guestStorage'
import type {
  SupportMessage,
  SupportSession,
  SupportThreadListRow,
} from '@/lib/support/types'

type SupportContextValue = {
  isOpen: boolean
  isBlockedByOverlay: boolean
  blockSupportForOverlay: () => () => void
  openPanel: () => void
  closePanel: () => void
  session: SupportSession | null
  pendingUserMessage: SupportMessage | null
  activeThreadId: string | null
  activeWriteToken: string | null
  threadList: SupportThreadListRow[]
  listLoading: boolean
  loading: boolean
  actionLoading: boolean
  error: string | null
  isTyping: boolean
  refreshThreadList: () => Promise<void>
  openThread: (_id: string) => Promise<void>
  newChat: () => Promise<void>
  selectOption: (_optionId: string) => Promise<void>
  sendChatMessage: (_text: string) => Promise<void>
  endChat: () => Promise<void>
  clearError: () => void
}

const SupportContext = createContext<SupportContextValue | null>(null)

const RESUME_MAX_THREAD_AGE_MS = 48 * 60 * 60 * 1000
const SUPPORT_TYPED_LIMIT_EXHAUSTED_TEXT =
  'You have reached the maximum of 5 typed questions in this chat.'
const SUPPORT_TYPED_LIMIT_CLOSED_NOTE =
  "You've reached the maximum question limit for this conversation. Please start a new conversation to continue getting help."

function isTypedSupportLimitExhausted(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  const msg = error.message.toLowerCase()
  return (
    msg.includes('maximum of 5 typed questions') ||
    msg.includes('question limit') ||
    msg.includes('maximum of 5')
  )
}

export function SupportProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [overlayBlockCount, setOverlayBlockCount] = useState(0)
  const isBlockedByOverlay = overlayBlockCount > 0

  const blockSupportForOverlay = useCallback(() => {
    setOverlayBlockCount((count) => count + 1)
    return () => setOverlayBlockCount((count) => Math.max(0, count - 1))
  }, [])

  useEffect(() => {
    if (isBlockedByOverlay && isOpen) {
      setIsOpen(false)
    }
  }, [isBlockedByOverlay, isOpen])
  const [session, setSession] = useState<SupportSession | null>(null)
  const [pendingUserMessage, setPendingUserMessage] = useState<SupportMessage | null>(
    null,
  )
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null)
  const [activeWriteToken, setActiveWriteToken] = useState<string | null>(null)
  const [threadList, setThreadList] = useState<SupportThreadListRow[]>([])
  const [listLoading, setListLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    clearLegacySupportGuestLocalStorage()
  }, [])

  // After login redirect: check sessionStorage for a pending support panel restore
  useEffect(() => {
    if (!isNormalUserSession()) return

    let stored: string | null = null
    try {
      stored = sessionStorage.getItem('support_panel_restore')
      if (stored) sessionStorage.removeItem('support_panel_restore')
    } catch {}

    if (!stored) return

    let parsed: { threadId?: string; writeToken?: string | null } | null = null
    try { parsed = JSON.parse(stored) } catch {}

    const threadId = parsed?.threadId
    if (!threadId) return

    const writeToken = parsed?.writeToken ?? null

    void (async () => {
      // Claim the guest thread first (link it to the now-logged-in user)
      if (writeToken) {
        try { await postClaimThread(threadId, writeToken) } catch {}
      }

      // Load the thread and open the panel
      setIsOpen(true)
      setActiveThreadId(threadId)
      setActiveWriteToken(null)
      setLoading(true)
      setError(null)
      try {
        const s = await getThread(threadId, null)
        setSession(s)
        setActiveThreadId(s.threadId)
      } catch {
        setSession(null)
      } finally {
        setLoading(false)
      }
    })()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // intentionally runs once on mount only

  const clearError = useCallback(() => setError(null), [])

  const refreshThreadList = useCallback(async () => {
    if (!isNormalUserSession()) {
      setThreadList([])
      return
    }
    setListLoading(true)
    setError(null)
    try {
      const rows = await getThreadListForUser()
      setThreadList(
        rows.map((r) => ({
          id: r.id,
          preview: r.preview,
          updatedAt: r.updatedAt,
          isGuest: false,
        })),
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load chat list')
      setThreadList([])
    } finally {
      setListLoading(false)
    }
  }, [])

  const openThread = useCallback(async (id: string) => {
    if (!id) return
    setPendingUserMessage(null)
    setActiveThreadId(id)
    if (isNormalUserSession()) {
      setActiveWriteToken(null)
    }
    setLoading(true)
    setError(null)
    try {
      const writeToken = isNormalUserSession() ? null : activeWriteToken
      const s = await getThread(id, writeToken)
      setSession(s)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open chat')
      setSession(null)
    } finally {
      setLoading(false)
    }
  }, [activeWriteToken])

  const newChat = useCallback(async () => {
    setPendingUserMessage(null)
    setActionLoading(true)
    setError(null)
    try {
      const s = await postCreateThread()
      setSession(s)
      setActiveThreadId(s.threadId)
      if (s.writeToken) {
        setActiveWriteToken(s.writeToken)
      } else {
        setActiveWriteToken(null)
      }
      if (isNormalUserSession()) {
        void refreshThreadList()
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start chat')
    } finally {
      setActionLoading(false)
    }
  }, [refreshThreadList])

  const openPanel = useCallback(() => {
    if (overlayBlockCount > 0) return
    setIsOpen(true)
    void (async () => {
      if (isNormalUserSession()) {
        setLoading(true)
        setError(null)
        try {
          const rows = await getThreadListForUser()
          setThreadList(
            rows.map((r) => ({
              id: r.id,
              preview: r.preview,
              updatedAt: r.updatedAt,
              isGuest: false,
            })),
          )
          if (rows.length === 0) {
            await newChat()
            return
          }
          const sorted = [...rows].sort(
            (a, b) =>
              new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
          )
          const mostRecent = sorted[0]
          const updatedAtMs = new Date(mostRecent.updatedAt).getTime()
          if (!Number.isFinite(updatedAtMs)) {
            await newChat()
            return
          }
          const ageMs = Date.now() - updatedAtMs
          if (ageMs > RESUME_MAX_THREAD_AGE_MS) {
            await newChat()
            return
          }
          const s = await getThread(mostRecent.id, null)
          if ((s.options?.length ?? 0) > 0 || s.closed) {
            setPendingUserMessage(null)
            setSession(s)
            setActiveThreadId(s.threadId)
            setActiveWriteToken(null)
            return
          }
          await newChat()
        } catch (e) {
          setError(e instanceof Error ? e.message : 'Could not open support')
          try {
            await newChat()
          } catch {
            /* ignore */
          }
        } finally {
          setLoading(false)
        }
        return
      }

      if (session) {
        return
      }

      if (activeThreadId && activeWriteToken) {
        setLoading(true)
        setError(null)
        try {
          const s = await getThread(activeThreadId, activeWriteToken)
          setPendingUserMessage(null)
          setSession(s)
          setActiveThreadId(s.threadId)
          if (s.writeToken) {
            setActiveWriteToken(s.writeToken)
          }
        } catch {
          setActiveThreadId(null)
          setActiveWriteToken(null)
          try {
            await newChat()
          } finally {
            setLoading(false)
          }
          return
        } finally {
          setLoading(false)
        }
        return
      }
      setLoading(true)
      setError(null)
      try {
        await newChat()
      } finally {
        setLoading(false)
      }
    })()
  }, [session, activeThreadId, activeWriteToken, newChat, overlayBlockCount])

  const closePanel = useCallback(() => {
    setIsOpen(false)
  }, [])

  const selectOption = useCallback(
    async (optionId: string) => {
      if (!activeThreadId || !optionId) return
      setPendingUserMessage(null)
      setActionLoading(true)
      setIsTyping(true)
      setError(null)
      try {
        const writeToken = isNormalUserSession() ? null : activeWriteToken
        const s = await postSelectOption(activeThreadId, optionId, writeToken)
        setSession(s)
        if (isNormalUserSession()) {
          void refreshThreadList()
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Action failed')
      } finally {
        setIsTyping(false)
        setActionLoading(false)
      }
    },
    [activeThreadId, activeWriteToken, refreshThreadList],
  )

  const sendChatMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim()
      if (!activeThreadId || !trimmed) return
      const pending: SupportMessage = {
        role: 'user',
        text: trimmed,
        createdAt: new Date().toISOString(),
      }
      setPendingUserMessage(pending)
      setActionLoading(true)
      setIsTyping(true)
      setError(null)
      try {
        const writeToken = isNormalUserSession() ? null : activeWriteToken
        const s = await postChatMessage(activeThreadId, trimmed, writeToken)
        setPendingUserMessage(null)
        setSession(s)
        if (isNormalUserSession()) {
          void refreshThreadList()
        }
      } catch (e) {
        setPendingUserMessage(null)
        if (isTypedSupportLimitExhausted(e)) {
          setError(null)
          setSession((prev) => {
            if (!prev || prev.closed) return prev
            const now = new Date().toISOString()
            const hasLimitNotice = prev.messages.some(
              (m) =>
                m.role === 'bot' &&
                (m.text.includes(SUPPORT_TYPED_LIMIT_EXHAUSTED_TEXT) ||
                  m.text.includes(SUPPORT_TYPED_LIMIT_CLOSED_NOTE)),
            )
            return {
              ...prev,
              closed: true,
              pendingEndConfirm: false,
              options: [],
              contextOptions: [],
              messages: hasLimitNotice
                ? prev.messages
                : [
                    ...prev.messages,
                    {
                      role: 'bot',
                      text: SUPPORT_TYPED_LIMIT_CLOSED_NOTE,
                      createdAt: now,
                    },
                  ],
            }
          })
        } else {
          setError(e instanceof Error ? e.message : 'Could not send message')
        }
      } finally {
        setIsTyping(false)
        setActionLoading(false)
      }
    },
    [activeThreadId, activeWriteToken, refreshThreadList],
  )

  const endChat = useCallback(async () => {
    if (!activeThreadId) return
    setPendingUserMessage(null)
    setActionLoading(true)
    setIsTyping(true)
    setError(null)
    try {
      const writeToken = isNormalUserSession() ? null : activeWriteToken
      const s = await postCloseThread(activeThreadId, writeToken)
      setSession(s)
      if (isNormalUserSession()) {
        void refreshThreadList()
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not end chat')
    } finally {
      setIsTyping(false)
      setActionLoading(false)
    }
  }, [activeThreadId, activeWriteToken, refreshThreadList])

  // Link in-memory guest thread to account right after they log in (no localStorage).
  useEffect(() => {
    if (!isNormalUserSession() || !activeThreadId || !activeWriteToken) {
      return
    }
    let cancelled = false
    void (async () => {
      try {
        await postClaimThread(activeThreadId, activeWriteToken)
        if (!cancelled) {
          setActiveWriteToken(null)
          void refreshThreadList()
        }
      } catch {
        // thread already owned or token invalid
      }
    })()
    return () => {
      cancelled = true
    }
  }, [activeThreadId, activeWriteToken, refreshThreadList])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const onAuth = () => {
      if (!isNormalUserSession()) {
        setPendingUserMessage(null)
        setSession(null)
        setActiveThreadId(null)
        setActiveWriteToken(null)
      }
      if (isOpen) {
        void refreshThreadList()
      }
    }
    window.addEventListener(AUTH_CHANGE_EVENT, onAuth)
    return () => window.removeEventListener(AUTH_CHANGE_EVENT, onAuth)
  }, [isOpen, refreshThreadList])

  const value: SupportContextValue = {
    isOpen,
    isBlockedByOverlay,
    blockSupportForOverlay,
    openPanel,
    closePanel,
    session,
    pendingUserMessage,
    activeThreadId,
    activeWriteToken,
    threadList,
    listLoading,
    loading,
    actionLoading,
    error,
    isTyping,
    refreshThreadList,
    openThread,
    newChat,
    selectOption,
    sendChatMessage,
    endChat,
    clearError,
  }

  return (
    <SupportContext.Provider value={value}>
      {children}
    </SupportContext.Provider>
  )
}

export function useSupport() {
  const ctx = useContext(SupportContext)
  if (!ctx) {
    throw new Error('useSupport must be used within SupportProvider')
  }
  return ctx
}

/** Hide and disable the support widget while a full-screen modal (e.g. practice question) is open. */
export function useSupportOverlayBlock(active: boolean) {
  const blockSupportForOverlay = useContext(SupportContext)?.blockSupportForOverlay

  useEffect(() => {
    if (!active || !blockSupportForOverlay) return
    return blockSupportForOverlay()
  }, [active, blockSupportForOverlay])
}
